import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import type {SteppedProgressBarProps} from './stepped-progress-bar.types';
import {SteppedProgressBarTheme} from './stepped-progress-bar.types';
import styles from './stepped-progress-bar.module.scss';

export function SteppedProgressBar({
  currentStep,
  numSteps,
  theme = SteppedProgressBarTheme.PRIMARY,
  labels = [],
  className = '',
  showCheckIconForCompletedStep = false,
  collapseForSmallerScreens = false,
}: SteppedProgressBarProps): ReactElement<any> {
  const [previousStep, setPreviousStep] = useState(1);
  const [currentAnimationIndex, setCurrentAnimationIndex] = useState<null | number>(null);
  const isForwards = useRef<boolean>(currentStep > previousStep);

  if (labels && labels.length > 0 && labels.length > numSteps) {
    throw new Error('the labels array cannot be larger than the number of steps passed to this component, please fix this');
  }

  const isWhiteTheme = (): boolean => {
    return theme === SteppedProgressBarTheme.WHITE;
  };

  const isIndexActiveForForwardsDirection = (index: number): boolean => {
    return isForwards.current && currentAnimationIndex !== null && currentAnimationIndex >= index;
  };

  const isIndexActiveForBackwardsDirection = (index: number): boolean => {
    return !isForwards.current && currentAnimationIndex !== null && currentAnimationIndex > index;
  };

  const isIndexActive = (index: number): boolean => {
    return isIndexActiveForForwardsDirection(index) || isIndexActiveForBackwardsDirection(index);
  };

  const isCompleted = (): boolean => {
    return showCheckIconForCompletedStep && currentStep === numSteps;
  };

  const getClassesForBar = (index: number): string => {
    const classes = [];

    if (currentAnimationIndex === null) {
      return '';
    }

    if (currentAnimationIndex >= index) {
      classes.push(styles.filled);

      if (isForwards.current && currentAnimationIndex === index) {
        classes.push(styles.forwardsFill);
      }

      if (!isForwards.current && currentAnimationIndex === index) {
        classes.push(styles.backwardsFill);
      }
    }

    return classes.join(' ');
  };

  const getClassesForCircle = (index: number): string => {
    const classes = [];

    if (isCompleted()) {
      return styles.full;
    }

    // first circle
    if (currentAnimationIndex === null && index === 0) {
      return styles.active;
    }

    if (currentAnimationIndex === null) {
      return '';
    }

    if (isForwards.current && currentAnimationIndex + 1 === index) {
      classes.push(styles.active);
    }

    if (!isForwards.current && currentAnimationIndex === index) {
      classes.push(styles.active);
    }

    if (isIndexActive(index)) {
      classes.push(styles.full);
    }

    return classes.join(' ');
  };

  const getClassesForLabel = (index: number): string => {
    if (isWhiteTheme()) {
      return 'content-body-white';
    }

    if (isCompleted()) {
      return 'content-primary';
    }

    if (currentAnimationIndex === null && index === 0) {
      return 'content-body';
    }

    if (currentAnimationIndex === null) {
      return 'content-disabled';
    }

    if (isForwards.current && currentAnimationIndex + 1 === index) {
      return 'content-body';
    }

    if (!isForwards.current && currentAnimationIndex === index) {
      return 'content-body';
    }

    if (isIndexActive(index)) {
      return 'content-primary';
    }

    return 'content-disabled';
  };

  const onAnimationEnd = (): void => {
    if (currentAnimationIndex === null) {
      return;
    }

    const nextIndex = isForwards.current ? currentAnimationIndex + 1 : currentAnimationIndex - 1;

    if (isForwards.current && nextIndex < currentStep - 1) {
      setCurrentAnimationIndex(nextIndex);
    }

    if (nextIndex < 0) {
      setCurrentAnimationIndex(null); // reset it
    }

    if (!isForwards.current && nextIndex >= currentStep - 1) {
      setCurrentAnimationIndex(nextIndex);
    }
  };

  const getClassesBasedOnTheme = (): string => {
    if (isWhiteTheme()) {
      return styles.whiteTheme;
    }

    return '';
  };

  const getCheckedStateSVG = (): ReactElement<any> => {
    return (
        <svg className={styles.checkedCircleSVG} xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
          <rect x="0.5" y="1" width="13" height="13" rx="6.5" fill="#3FBCE8"/>
          <rect x="0.5" y="1" width="13" height="13" rx="6.5" stroke="#3FBCE8"/>
          <path d="M9.69281 5.64844L5.99018 9.35107L4.30716 7.66806" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
  };

  useEffect(() => {
    if (previousStep !== currentStep) {
      isForwards.current = currentStep > previousStep;
      setCurrentAnimationIndex(isForwards.current ? previousStep - 1 : previousStep - 2);
      setPreviousStep(currentStep);
    }
  }, [currentStep]);

  if (collapseForSmallerScreens) {
    return (
      <div className={`flex-row-align-center flex-justify-center ${className}`}>
        {Array.from({length: numSteps}, (_, i) => {
          const isFilled = i < currentStep;

          return (
            <div
              key={`collapsed-circle-${i}`}
              className={`
                ${styles.collapsedCircle} 
                ${isFilled ? styles.collapsedFilled : ''} 
                ${i !== numSteps - 1 ? 'spacing-m-r-2' : ''}
              `}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className={`${styles.progressBarContainer} flex-row-align-center flex-justify-between ${className} ${getClassesBasedOnTheme()}`}>
      {Array.from({length: numSteps}, (_, index) => {
        return (
          <React.Fragment key={`step-${index}`}>
            <div className={styles.circleContainer}>
              <div className={`${styles.circle} radius-round ${getClassesForCircle(index)} flex-center`}>
                <div className={`${styles.circleInnerDot} radius-round`} />
                {!isWhiteTheme() ? getCheckedStateSVG() : null}
              </div>
              {labels[index] ? <Text val={labels[index]} size={TextSize.XXSMALL} className={`${styles.label} ${getClassesForLabel(index)}`} bold={isIndexActive(index)} /> : null}
            </div>

            {index < numSteps - 1 ? (
              <div className={`${styles.miniBarContainer} flex-1`}>
                <div className={`${styles.progressBar} ${getClassesForBar(index)}`} onAnimationEnd={currentAnimationIndex === index ? onAnimationEnd : undefined} />
              </div>
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}
